<template>
  <v-container fluid class="ma-4 mt-3 px-8">
    <v-row justify="start" align="center">
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-text-field
          v-model="form.usn_id"
          outlined
          dense
          clearable
          hide-details="auto"
          label="USN"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-text-field
          v-model="form.email"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Email"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-text-field
          v-model="form.mobile_no"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Mobile Number"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-3">
        <v-text-field
          v-model="form.current_term_no"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Current Term"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.year_batch"
          :items="batches"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Batch"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.program_id"
          :items="programs"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Programme"
          :item-text="
            (item) =>
              `${item.degree.short_name} ${
                item.specialization ? `- ${item.specialization}` : ''
              }`
          "
          item-value="id"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0" v-if="showPartner()">
        <v-autocomplete
          v-model="form.partner_id"
          :items="partners"
          item-text="name"
          item-value="id"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Partner"
        ></v-autocomplete>
      </v-col>

      <v-col cols="12" md="3" class="py-0 my-0">
        <v-text-field
          v-model="form.employee_id"
          outlined
          dense
          clearable
          hide-details="auto"
          label="Employee ID"
        ></v-text-field>
      </v-col>
      <v-col cols="12" md="3" class="py-0 my-0">
        <v-autocomplete
          v-model="form.student_status"
          :items="statuses"
          outlined
          dense
          hide-details="auto"
          clearable
          label="Status"
          item-text="name"
          item-value="id"
        ></v-autocomplete>
      </v-col>
      <v-col cols="12" md="2" class="d-flex justify-start">
        <v-btn
          class="accent white--text"
          :loading="loading"
          @click="init()"
          :disabled="loading"
        >
          Search
        </v-btn>
        <v-btn
          class="accent white--text ml-2"
          :disabled="!students.length"
          :loading="loading"
          @click="download()"
        >
          <v-icon>mdi-download</v-icon> Download
        </v-btn>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <v-sheet outlined>
          <v-data-table
            :headers="headers"
            :items="students"
            fixed-header
            item-key="student_detail_id"
          >
            <template v-slot:no-data>
              <div class="text-center">No data available</div>
            </template>
            <template #[`item.year`]="{ item }">
              {{ item.year }} - {{ item.batch }}
            </template>
            <template #[`item.usn_id`]="{ item }">
              <span
                @click="openDialog(item)"
                style="cursor: pointer; text-decoration: underline"
              >
                <a>
                  {{ item.usn_id }}
                </a>
              </span>
            </template>
            <template #[`item.actions`]="{ item }">
              <v-icon
                small
                class="mr-2"
                color="accent"
                :disabled="item.current_status == 'Discontinued'"
                @click="editDialog(item)"
              >
                mdi-pencil
              </v-icon>
            </template>
          </v-data-table>
        </v-sheet>
      </v-col>
    </v-row>

    <!-- Use the new StudentDialog component -->
    <StudentDialog
      :dialog="dialog"
      :student-data="selectedStudent"
      @close-dialog="closeDialog"
    />

    <v-dialog v-model="confirmationDialog" width="500" persistent>
      <ConfirmationDialog
        v-if="confirmationDialog"
        :confirmationText="confirmationText"
        @closeDialog="confirmationDialog = false"
        @saveDialog="updateStudent()"
      />
    </v-dialog>
  </v-container>
</template>

<script>
import { rules } from "@/helpers/rules";
import { studentDetailHeaders } from "@/constants/headers";
import ConfirmationDialog from "@/components/confirmationDialog.vue";
import StudentDialog from "@/components/studentDialog.vue";

export default {
  components: {
    ConfirmationDialog,
    StudentDialog, // Register the component
  },
  data() {
    return {
      form: {
        student_status: "A",
      },
      programs: [],
      partners: [],
      statuses: [
        { name: "Active", id: "A" },
        { name: "Inactive", id: "I" },
        { name: "Discontinued", id: "T" },
        { name: "Graduated", id: "X" },
      ],
      rules,
      loading: false,
      batches: [],
      headers: studentDetailHeaders,
      students: [],
      selectedStudent: {},
      dialog: false,
      editStatus: {
        remarks: null,
        termNo: null,
        changedStatus: null,
      },
      program: {},
      confirmationDialog: false,
      readmitStatus: {},
    };
  },
  watch: {},
  computed: {
    currentUser() {
      return this.$store.state.currentUser;
    },
    formattedStatus() {
      if (this.currentUser.partner_id != 1) {
        return ["Exit/Termination"];
      } else if (this.selectedStudent.current_status == "Active") {
        return ["Inactive", "Discontinued"];
      } else {
        return ["Re-Admission", "Discontinued"];
      }
    },

    confirmationText() {
      const status = this.editStatus.changedStatus;

      const messages = {
        "Exit/Termination":
          "Are you sure you want to terminate the selected student?",
        Terminate: "Are you sure you want to terminate the selected student?",
        "Re-Admission":
          "Are you sure you want to re-admit the selected student?",
        Inactive:
          "Are you sure you want to mark the selected student as inactive?",
      };

      return messages[status] || "";
    },
  },
  async created() {
    this.$store.commit("setBreadcrumbs", [{ text: "Student Details" }]);
    await this.generateBatchList();
    this.getPrograms();
    this.showPartnerFilter();
    this.init();
  },
  methods: {
    async getStudents() {
      this.loading = true;
      const queryString = this.objectToQueryParams({
        ...this.form,
      });
      try {
        const res = await this.$axios.get(
          `/list_status_students?${queryString}`,
        );
        this.students = res.data.data.sort((a, b) => b.usn_id - a.usn_id);

        this.loading = false;
      } catch (error) {
        this.loading = false;
        console.log(error);
      }
    },
    objectToQueryParams(obj) {
      if (obj.year_batch) {
        const [year, batch] = obj.year_batch.split("-");
        obj.year = year;
        obj.batch = batch;
        delete obj.year_batch;
      }

      return Object.keys(obj)
        .filter(
          (key) =>
            obj[key] !== undefined && obj[key] !== null && obj[key] !== "",
        ) // Remove undefined/null values
        .map(
          (key) => encodeURIComponent(key) + "=" + encodeURIComponent(obj[key]),
        )
        .join("&");
    },
    async init() {
      this.getStudents();
    },
    async getPrograms() {
      let endpoint = "/programs";
      if (this.currentUser.partner_id != 1)
        endpoint = `/get_partner_programs/${this.currentUser.partner_id}`;
      try {
        const response = await this.$axios.get(endpoint);
        this.programs = response.data.data.sort((a, b) =>
          a.degree?.short_name.localeCompare(b.degree?.short_name),
        );
      } catch (error) {
        console.log(error);
      }
    },
    showPartner() {
      return (
        this.currentUser.partner_id == 1 ||
        (this.currentUser.role === "Partner" &&
          this.partners.length > 1 &&
          this.currentUser.partner_level != "S")
      );
    },
    async getPartners() {
      try {
        const response = await this.$axios.get("/partners");
        this.partners = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async showPartnerFilter() {
      if (this.currentUser.partner_id != 1) {
        this.form.partner_id = this.currentUser.partner_id;
      }
      await this.getPartners();
      if (
        this.currentUser.role === "Partner" &&
        this.currentUser.partner_level === "P"
      ) {
        this.partners = this.partners.filter(
          (item) =>
            item.id === this.currentUser.partner_id ||
            item.parent_id === this.currentUser.partner_id,
        );
      }
    },
    async generateBatchList() {
      try {
        const response = await this.$axios.get("/get_year_batch");
        this.batches = response.data.data;
      } catch (error) {
        console.log(error);
      }
    },
    async editDialog(item) {
      this.selectedStudent = { ...item };
      if (this.selectedStudent.current_status == "Inactive") {
        const response = await this.$axios.get(
          `/programs/${this.selectedStudent.program_id}`,
        );
        this.program = response.data.data;
      }
      if (this.currentUser.partner_id != 1) {
        this.editStatus.changedStatus = "Exit/Termination";
      }
      this.dialog = true;
    },
    closeDialog() {
      this.editStatus = {
        remarks: null,
        termNo: null,
        changedStatus: null,
      };
      this.dialog = false;
      this.confirmationDialog = false;
      this.selectedStudent = {};
      this.readmitStatus = {};
      this.program = {};
    },
    openDialog(item) {
      this.getStudentDetail(item);
      this.dialog = true;
    },
    async getStudentDetail(item) {
      const response = await this.$axios.get(`/student_detail/${item.usn_id}`);
      this.selectedStudent = {
        ...response.data.data,
        current_status:
          response.data.data?.current_status || item.current_status,
      };
      console.log(this.selectedStudent);
    },
    async download() {
      this.loading = true;
      const queryString = this.objectToQueryParams({ ...this.form });
      try {
        const res = await this.$axios.get(
          `/download_student_details?${queryString}`,
          { responseType: "blob" },
        );
        const blob = new Blob([res.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "Student Details.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (e) {
        console.log(e);
        this.$store.commit("showSnackbar", {
          message: "Download failed",
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped></style>
